/**
 * Endcap or Connectable — required when shown (same ATC gate as power source).
 * Companion line items are added with the main product in product-form.js
 * so the cart drawer refreshes once with every line.
 */
if (!customElements.get('endcaps-selector')) {
  class EndcapsSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.errorEl = this.querySelector('.endcaps__error');
      this.productSection = this.closest('.js-product');
      this.form = this.productSection?.querySelector('.js-product-form-main');

      this.onSelectChange = () => this.hideError();
      this.onFormSubmit = (evt) => this.handleFormSubmit(evt);
      this.onVariantChange = () => this.clearSelection();

      this.select?.addEventListener('change', this.onSelectChange);

      if (this.form) {
        this.form.addEventListener('submit', this.onFormSubmit, true);
      }

      this.productSection?.addEventListener('on:variant:change', this.onVariantChange);

      // No default / browser-restored selection — conscious pick required.
      this.clearSelection();
    }

    disconnectedCallback() {
      this.select?.removeEventListener('change', this.onSelectChange);
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      this.productSection?.removeEventListener('on:variant:change', this.onVariantChange);
    }

    get select() {
      return this.querySelector('[data-endcap-select]');
    }

    getSelectedOption() {
      return this.select?.selectedOptions?.[0] || null;
    }

    /**
     * @returns {boolean}
     */
    hasEndcapOptions() {
      return Boolean(this.select) && this.select.querySelectorAll('option[data-endcap-variant-id]:not([value=""])').length > 0;
    }

    /**
     * @returns {string|null}
     */
    getSelectedEndcapVariantId() {
      const option = this.getSelectedOption();
      if (!option || !option.value) return null;
      return option.dataset.endcapVariantId || null;
    }

    clearSelection() {
      const select = this.select;
      if (select) select.value = '';
      this.hideError();
    }

    showError(message) {
      this.classList.add('is-invalid');
      if (!this.errorEl) return;
      this.errorEl.hidden = false;
      if (message) this.errorEl.textContent = message;
    }

    hideError() {
      this.classList.remove('is-invalid');
      if (this.errorEl) this.errorEl.hidden = true;
    }

    /**
     * Validate before product-form submit handler runs.
     * @param {SubmitEvent} evt
     */
    handleFormSubmit(evt) {
      if (!this.isConnected || !this.hasEndcapOptions()) return;

      if (!this.getSelectedEndcapVariantId()) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.showError('Please select an Endcap');
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.select?.focus();
        return;
      }

      this.hideError();
    }
  }

  customElements.define('endcaps-selector', EndcapsSelector);
}
